import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, UserCheck, UserX, Building2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../lib/use-toast';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';

export default function UsersPage() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { toast } = useToast();
  const isMaster = user?.role === 'master';

  const [users, setUsers] = useState([]);
  const [lessors, setLessors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    senha: '',
    telefone: '',
    role: 'locadora',
    companyId: '',
    ativo: true
  });

  const fetchUsers = async () => {
    try {
      const response = await api.get('/auth/users');
      // Se não for master, filtrar apenas usuários da sua locadora
      const filteredUsers = isMaster 
        ? response.data 
        : response.data.filter(u => u.companyId?._id === user.companyId?._id);
      setUsers(filteredUsers);
    } catch (error) {
      toast({
        title: t('usersPage.errors.loadUsers'),
        description: error.response?.data?.error || error.message,
        variant: 'destructive'
      });
    }
  };

  const fetchLessors = async () => {
    try {
      if (isMaster) {
        const response = await api.get('/locadoras');
        setLessors(response.data);
      } else if (user?.companyId) {
        // Para usuários de locadora, usa apenas sua empresa
        setLessors([{
          _id: user.companyId._id,
          nomeFantasia: user.companyId.nomeFantasia
        }]);
      }
    } catch (error) {
      toast({
        title: t('usersPage.errors.loadLessors'),
        description: error.response?.data?.error || error.message,
        variant: 'destructive'
      });
    }
  };

  useEffect(() => {
    Promise.all([fetchUsers(), fetchLessors()])
      .finally(() => setLoading(false));
  }, [isMaster, user]);

  const handleOpenDialog = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        nome: user.nome,
        email: user.email,
        senha: '',
        telefone: user.telefone || '',
        role: user.role,
        companyId: user.companyId?._id || '',
        ativo: user.ativo
      });
    } else {
      setEditingUser(null);
      // Se não for master, pre-seleciona a locadora do usuário
      const defaultCompanyId = !isMaster && user?.companyId?._id ? user.companyId._id : '';
      const defaultRole = isMaster ? 'locadora' : 'locadora'; // Locadoras só criam usuários tipo locadora
      
      setFormData({
        nome: '',
        email: '',
        senha: '',
        telefone: '',
        role: defaultRole,
        companyId: defaultCompanyId,
        ativo: true
      });
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingUser(null);
    setFormData({
      nome: '',
      email: '',
      senha: '',
      telefone: '',
      role: 'locadora',
      companyId: '',
      ativo: true
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validações
    if (!formData.nome || !formData.email) {
      toast({
        title: t('usersPage.validation.requiredFields'),
        description: t('usersPage.validation.nameEmailRequired'),
        variant: 'destructive'
      });
      return;
    }

    if (!editingUser && !formData.senha) {
      toast({
        title: t('usersPage.validation.passwordRequired'),
        description: t('usersPage.validation.passwordRequiredDesc'),
        variant: 'destructive'
      });
      return;
    }

    if (formData.role === 'locadora' && !formData.companyId) {
      toast({
        title: t('usersPage.validation.lessorRequired'),
        description: t('usersPage.validation.lessorRequiredDesc'),
        variant: 'destructive'
      });
      return;
    }

    // Locadoras não podem criar usuários master
    if (!isMaster && formData.role === 'master') {
      toast({
        title: t('usersPage.validation.permissionDenied'),
        description: t('usersPage.validation.onlyMasterCanCreateMaster'),
        variant: 'destructive'
      });
      return;
    }

    // Locadoras só podem criar usuários da própria empresa
    if (!isMaster && formData.companyId !== user?.companyId?._id) {
      toast({
        title: t('usersPage.validation.permissionDenied'),
        description: t('usersPage.validation.onlyOwnCompanyUsers'),
        variant: 'destructive'
      });
      return;
    }

    try {
      const payload = { ...formData };
      
      // Remover senha vazia em edição
      if (editingUser && !payload.senha) {
        delete payload.senha;
      }

      // Remover companyId se for master
      if (payload.role === 'master') {
        delete payload.companyId;
      }

      if (editingUser) {
        await api.put(`/auth/users/${editingUser._id}`, payload);
        toast({
          title: t('usersPage.success.userUpdated'),
          description: t('usersPage.success.userUpdatedDesc')
        });
      } else {
        await api.post('/auth/register', payload);
        toast({
          title: t('usersPage.success.userCreated'),
          description: t('usersPage.success.userCreatedDesc')
        });
      }

      handleCloseDialog();
      fetchUsers();
    } catch (error) {
      toast({
        title: editingUser ? t('usersPage.errors.updateUser') : t('usersPage.errors.createUser'),
        description: error.response?.data?.error || error.message,
        variant: 'destructive'
      });
    }
  };

  const handleToggleStatus = async (userId, currentStatus) => {
    try {
      await api.patch(`/auth/users/${userId}/status`, { ativo: !currentStatus });
      toast({
        title: t('usersPage.success.statusUpdated'),
        description: t('usersPage.success.statusUpdatedDesc', { 
          status: !currentStatus ? t('usersPage.status.activated') : t('usersPage.status.deactivated') 
        })
      });
      fetchUsers();
    } catch (error) {
      toast({
        title: t('usersPage.errors.updateStatus'),
        description: error.response?.data?.error || error.message,
        variant: 'destructive'
      });
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm(t('usersPage.confirmDelete'))) {
      return;
    }

    try {
      await api.delete(`/auth/users/${userId}`);
      toast({
        title: t('usersPage.success.userDeleted'),
        description: t('usersPage.success.userDeletedDesc')
      });
      fetchUsers();
    } catch (error) {
      toast({
        title: t('usersPage.errors.deleteUser'),
        description: error.response?.data?.error || error.message,
        variant: 'destructive'
      });
    }
  };

  if (!isMaster && !user?.companyId) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">
              {t('usersPage.errors.noCompanyAssociation')}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <p>{t('usersPage.loading')}</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">
            {isMaster ? t('usersPage.title') : t('usersPage.title')}
          </h1>
          <p className="text-muted-foreground mt-1">
            {isMaster 
              ? t('usersPage.subtitle')
              : t('usersPage.subtitleLessor', { company: user?.companyId?.nomeFantasia })
            }
          </p>
        </div>
        <Button onClick={() => handleOpenDialog()}>
          <Plus className="h-4 w-4 mr-2" />
          {t('usersPage.newUser')}
        </Button>
      </div>

      {/* Tabela de Usuários */}
      <Card>
        <CardHeader>
          <CardTitle>{t('usersPage.table.title')}</CardTitle>
          <CardDescription>
            {t('usersPage.table.usersCount', { count: users.length })}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('usersPage.table.headers.name')}</TableHead>
                <TableHead>{t('usersPage.table.headers.email')}</TableHead>
                <TableHead>{t('usersPage.table.headers.type')}</TableHead>
                <TableHead>{t('usersPage.table.headers.lessor')}</TableHead>
                <TableHead>{t('usersPage.table.headers.status')}</TableHead>
                <TableHead className="text-right">{t('usersPage.table.headers.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u._id}>
                  <TableCell className="font-medium">{u.nome}</TableCell>
                  <TableCell>{u.email}</TableCell>
                  <TableCell>
                    <Badge variant={u.role === 'master' ? 'default' : 'secondary'}>
                      {u.role === 'master' ? t('usersPage.table.userTypes.master') : t('usersPage.table.userTypes.lessor')}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {u.companyId ? (
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-muted-foreground" />
                        {u.companyId.nomeFantasia}
                      </div>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant={u.ativo ? 'success' : 'destructive'}>
                      {u.ativo ? t('usersPage.table.status.active') : t('usersPage.table.status.inactive')}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleToggleStatus(u._id, u.ativo)}
                        title={u.ativo ? t('usersPage.table.actions.deactivate') : t('usersPage.table.actions.activate')}
                      >
                        {u.ativo ? (
                          <UserX className="h-4 w-4" />
                        ) : (
                          <UserCheck className="h-4 w-4" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleOpenDialog(u)}
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(u._id)}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Dialog de Criar/Editar */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <form onSubmit={handleSubmit}>
            <DialogHeader>
              <DialogTitle>
                {editingUser ? t('usersPage.dialog.editUser') : t('usersPage.dialog.newUser')}
              </DialogTitle>
              <DialogDescription>
                {editingUser 
                  ? t('usersPage.dialog.editUserDesc')
                  : t('usersPage.dialog.newUserDesc')
                }
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="nome">{t('usersPage.dialog.fields.name')} *</Label>
                <Input
                  id="nome"
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  placeholder={t('usersPage.dialog.placeholders.name')}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">{t('usersPage.dialog.fields.email')} *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder={t('usersPage.dialog.placeholders.email')}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="senha">
                  {t('usersPage.dialog.fields.password')} {editingUser ? t('usersPage.dialog.passwordOptional') : '*'}
                </Label>
                <Input
                  id="senha"
                  type="password"
                  value={formData.senha}
                  onChange={(e) => setFormData({ ...formData, senha: e.target.value })}
                  placeholder="••••••••"
                  required={!editingUser}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="telefone">{t('usersPage.dialog.fields.phone')}</Label>
                <Input
                  id="telefone"
                  value={formData.telefone}
                  onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
                  placeholder={t('usersPage.dialog.placeholders.phone')}
                />
              </div>

              {isMaster ? (
                <div className="space-y-2">
                  <Label htmlFor="role">{t('usersPage.dialog.fields.userType')} *</Label>
                  <Select 
                    value={formData.role} 
                    onValueChange={(value) => setFormData({ ...formData, role: value, companyId: value === 'master' ? '' : formData.companyId })}
                  >
                    <SelectTrigger id="role">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="master">{t('usersPage.dialog.userTypes.master')}</SelectItem>
                      <SelectItem value="locadora">{t('usersPage.dialog.userTypes.lessor')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>{t('usersPage.dialog.fields.userType')}</Label>
                  <p className="text-sm">{t('usersPage.dialog.userTypes.lessor')}</p>
                </div>
              )}

              {isMaster ? (
                formData.role === 'locadora' && (
                  <div className="space-y-2">
                    <Label htmlFor="companyId">{t('usersPage.dialog.fields.lessor')} *</Label>
                    <Select 
                      value={formData.companyId} 
                      onValueChange={(value) => setFormData({ ...formData, companyId: value })}
                    >
                      <SelectTrigger id="companyId">
                        <SelectValue placeholder={t('usersPage.dialog.placeholders.selectLessor')} />
                      </SelectTrigger>
                      <SelectContent>
                        {lessors.map((lessor) => (
                          <SelectItem key={lessor._id} value={lessor._id}>
                            {lessor.nomeFantasia}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )
              ) : (
                <div className="space-y-2">
                  <Label>{t('usersPage.dialog.fields.lessor')}</Label>
                  <p className="text-sm">{user?.companyId?.nomeFantasia}</p>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                {t('usersPage.dialog.buttons.cancel')}
              </Button>
              <Button type="submit">
                {editingUser ? t('usersPage.dialog.buttons.update') : t('usersPage.dialog.buttons.create')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
